import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import ReportToolbar from './ReportToolbar';
import { exportStyledExcel } from '../../../utils/excelExport';

//const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const fmtDate = (value) => {
  return value || "-";
};

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PurchasePriceList = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'PurchaseReportApi',
        `/PurchasePriceListDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch purchase price list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, token]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r => (r.ItemName || '').toLowerCase().includes(q));
  }, [data, search]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'PurchasePriceList', 0);
    if (!filtered.length) return;
    const colHeaders = ['#', 'Item Name', 'Current Price', 'Current Date', 'Previous Price', 'Previous Date', 'Change'];
    const rows = filtered.map((r, i) => {
      const curr = parseFloat(r.CurrentPrice) || 0;
      const prev = parseFloat(r.PreviousPrice) || 0;
      const change = prev > 0 ? (((curr - prev) / prev) * 100).toFixed(2) + '%' : '-';
      return [i + 1, r.ItemName, curr, fmtDate(r.CurrentDate), prev, fmtDate(r.PreviousDate), change];
    });

    await exportStyledExcel({
      reportTitle: 'PURCHASE PRICE LIST REPORT',
      sheetName: 'Price List',
      fileName: `PurchasePriceList_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: colHeaders,
      rows,
      columnWidths: [4,30,14,14,14,14,10],
    });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
  const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

  const PRIMARY = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT = '#e0f7fa';

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading purchase price list..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <ReportToolbar
          title="Purchase Price List"
          onBack={() => history.push('/ReportsHub')}
          search={search}
          onSearchChange={setSearch}
          fromDate={fromDate}
          onFromDate={setFromDate}
          toDate={toDate}
          onToDate={setToDate}
          onRefresh={fetchReport}
          loading={loading}
          onExport={handleExport}
          exportDisabled={!filtered.length}
          searchPlaceholder="Search item..."
        />

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Item Name</th>
                <th style={{ ...th, textAlign: 'right' }}>Current Price</th>
                <th style={th}>Current Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Previous Price</th>
                <th style={th}>Previous Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Change</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const curr = parseFloat(r.CurrentPrice) || 0;
                  const prev = parseFloat(r.PreviousPrice) || 0;
                  const diff = curr - prev;
                  const pct  = prev > 0 ? ((diff / prev) * 100).toFixed(2) : null;
                  const up   = diff > 0;
                  // const down = diff < 0; // reserved for future use

                  return (
                    <tr key={r.ItemId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>{r.ItemName}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: PRIMARY }}>₹{curr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.CurrentDate)}</td>
                      <td style={{ ...tdR, color: '#64748b' }}>
                        {prev > 0 ? `₹${prev.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.PreviousDate)}</td>
                      <td style={tdC}>
                        {pct === null ? (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>New</span>
                        ) : diff === 0 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#64748b', fontSize: 11 }}>
                            <TrendingFlat sx={{ fontSize: 14 }} /> No Change
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 600, color: up ? '#dc2626' : '#059669' }}>
                            {up ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
                            {up ? '+' : ''}{pct}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchasePriceList;


